import React from "react";
import { useUscSelector } from "../../../../redux/store";
import DescriptionLines from "../../common/DescriptionLines";
import { UscContext, UscVehicleSaleType } from "../../../../../shared-logic/types/UscCommonTypes";
import { getVehicleSaleType, vehicleHasWarranty } from "../../../../../shared-logic/utils/uscUtils";
import { DfVehicleStatus } from "../../../../../shared-logic/utils/dfConstants";
import Text from "../../../../../shared-components/toyota/styles/atoms/Text";
import Banner from "../../styles/molecules/Banner";
import Icon from "../../../../../shared-components/toyota/styles/atoms/Icon";
import * as BannerStyles from "../../styles/molecules/Banner.styles";
import { getWarrantyBannerDescriptionItems } from "../../../../utils/helpers";

const saleTypeIcons: Record<UscVehicleSaleType, JSX.Element> = {
    [UscVehicleSaleType.Private]: <Icon variant="profile-ring" />,
    [UscVehicleSaleType.Approved]: <Icon variant="approved" />,
    [UscVehicleSaleType.NonApproved]: <Icon variant="non-approved" />,
};

// In used cars this banner is used as a banner for describing the warranty
// The banner will be hidden when there is no valid warranty
// In stock cars there are no warranties so NMSC's are using this banner for other purposes.
const WarrantyBanner = (): JSX.Element | null => {
    const vehicle = useUscSelector((state) => state.vehicle);
    const uscSettings = useUscSelector((state) => state.uscSettings);
    const enableUcProgramBadges = useUscSelector((state) => state.uscSettings.enableUcProgramBadges);
    const enablePrivatelySoldVehicles = useUscSelector((state) => state.uscSettings.enablePrivatelySoldVehicles);
    const commonSettings = useUscSelector((state) => state.commonSettings);

    // Hide warranty where is no valid warranty but only for used cars
    const hideWarranty = !vehicleHasWarranty(vehicle.result) && vehicle.type === UscContext.Used;
    const customerPromiseBanner = getWarrantyBannerDescriptionItems(
        uscSettings,
        vehicle.result,
        commonSettings.brand,
        vehicle.type,
        commonSettings,
    );

    if (!vehicle.result) return null;

    const { vehicleSaleType, label: vehicleSaleTypeLabel } = getVehicleSaleType(
        vehicle.result,
        commonSettings.brand,
        enablePrivatelySoldVehicles,
        commonSettings,
    );

    if (hideWarranty || !customerPromiseBanner?.bannerItems?.length) return null;

    const { bannerItems, title: bannerTitle, description: bannerDescription } = customerPromiseBanner;

    const isSold = vehicle.result?.vehicleStatus.code === DfVehicleStatus.Sold;
    if (isSold) return null;

    return (
        <Banner variant="warranty-sale-type" title={bannerTitle}>
            {enableUcProgramBadges && (
                <BannerStyles.Header>
                    {saleTypeIcons[vehicleSaleType]}
                    {vehicleSaleTypeLabel}
                </BannerStyles.Header>
            )}
            <BannerStyles.Body>
                {bannerDescription && (
                    <Text tag="p" spacing={{ mb: "spacing-3" }}>
                        {bannerDescription}
                    </Text>
                )}

                <DescriptionLines listItems={bannerItems} vehicleSaleType={vehicleSaleType} />
            </BannerStyles.Body>
        </Banner>
    );
};

export default WarrantyBanner;
